import React, { useEffect, useState } from 'react'

import { billingErrorMessage, useBillingProfile, useUpdateBillingProfile } from '@/src/hooks/queries/billingEngine/useBillingDocuments'
import { useCurrentTeamId } from '@/src/hooks/useCurrentTeamId'
import type { BillingProfile } from '@/src/types/billingEngine'

import { BillingDetailsFields, BillingDetailsSummary } from './BillingDetailsFields'

export const InvoicePreference = ({ value, onChange, disabled = false }: { value: boolean; onChange: (value: boolean) => void; disabled?: boolean }) => (
  <label className='tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-gray-700'>
    <input type='checkbox' className='tw-mt-1 tw-accent-emerald-600' checked={value} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    <span>
      Always include invoices for credit purchases<span className='tw-mt-1 tw-block tw-text-xs tw-text-gray-500'>Includes future auto-reloads. You can change this for individual purchases.</span>
    </span>
  </label>
)

export default function BillingDetailsSection() {
  const query = useBillingProfile()
  const update = useUpdateBillingProfile()
  const teamId = useCurrentTeamId()
  const [draft, setDraft] = useState<BillingProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const profile = query.data?.billing_profile
  useEffect(() => {
    setDraft(null)
    setError(null)
  }, [teamId])

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!draft) return
    setError(null)
    try {
      await update.mutateAsync(draft)
      setDraft(null)
    } catch (error) {
      setError(billingErrorMessage(error))
    }
  }

  return (
    <section className='tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-5 lg:tw-border-l lg:tw-border-t-0 lg:tw-pl-6 lg:tw-pt-0' aria-label='Billing details'>
      <div className='tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3'>
        <h4 className='tw-m-0 tw-text-sm tw-font-semibold tw-text-ink'>Billing details</h4>
        {profile && !draft && (
          <button
            type='button'
            className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-brand-strong'
            onClick={() => {
              setDraft(structuredClone(profile))
              setError(null)
            }}
          >
            {profile.address.line1 ? 'Edit' : 'Add billing details'}
          </button>
        )}
      </div>
      {query.isError ? (
        <div role='alert' className='tw-text-sm tw-text-red-600'>
          Unable to load billing details.{' '}
          <button type='button' className='tw-underline' onClick={() => query.refetch()}>
            Retry
          </button>
        </div>
      ) : !profile ? (
        <p className='tw-text-sm tw-text-gray-500'>Loading billing details…</p>
      ) : draft ? (
        <form onSubmit={save} className='tw-space-y-4'>
          <BillingDetailsFields value={draft} onChange={setDraft} taxIdTypes={query.data?.tax_id_types || []} disabled={update.isLoading} />
          <InvoicePreference disabled={update.isLoading} value={draft.invoice_credit_purchases} onChange={(value) => setDraft({ ...draft, invoice_credit_purchases: value })} />
          <p className='tw-text-xs tw-text-gray-500'>Saved for this team’s future invoices. Existing invoices keep their original details.</p>
          {error && (
            <p role='alert' className='tw-text-sm tw-text-red-600'>
              {error}
            </p>
          )}
          <div className='tw-flex tw-gap-3'>
            <button type='submit' disabled={update.isLoading} className='tw-rounded-[2px] tw-border-0 tw-bg-ink tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white disabled:tw-opacity-50'>
              {update.isLoading ? 'Saving…' : 'Save billing details'}
            </button>
            <button type='button' disabled={update.isLoading} className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-gray-600' onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <BillingDetailsSummary profile={profile} />
          <p className='tw-mb-0 tw-mt-3 tw-text-xs tw-text-gray-500'>
            {profile.invoice_credit_purchases ? 'Invoices included for credit purchases and auto-reloads.' : 'You can request an invoice when adding funds.'}
          </p>
        </>
      )}
    </section>
  )
}
